'use client';

import { useQuery, useMutation } from '@tanstack/react-query';

import { notify, objectToFormData } from 'src/utils/helper';
import { JWT_STORAGE_KEY, USER_ID_STORAGE_KEY } from 'src/utils/constants';

import { restClient, orderClient } from 'src/lib/api';
import { useLocale } from 'src/locales';

/**
 * Add Contact details to order (Called in Order details screen)
 * @returns
 */
export const usePostOrderContactDetails = () => {
  const { t } = useLocale();
  const mutationFn = (payload) =>
    orderClient
      .post('api/v1/order/addMobileNumbersInOrders', payload, {
        headers: {
          token: sessionStorage.getItem(JWT_STORAGE_KEY),
        },
      })
      .then((res) => res?.data);
  return useMutation({
    mutationFn,
    onSuccess: () => {},
  });
};

/**
 * Save Contact Details and Notes (Called After Placing order on home screen)
 * @returns
 */
export const usePostOrderContactDetailsAndNotes = () => {
  const mutationFn = (payload) =>
    restClient
      .post('addMobileNumberAndUploadInvoiceImages', objectToFormData(payload), {
        headers: {
          token: sessionStorage.getItem(JWT_STORAGE_KEY),
        },
      })
      .then((res) => res?.data);
  return useMutation({
    mutationFn,
    onSuccess: () => {},
  });
};

/**
 * Save Locations Quick Access On Home screen
 * @returns
 */
export const useSaveLocation = () => {
  const mutationFn = (payload) =>
    // orderClient
    //   .post('api/v1/order/addFavoriteLocation', payload, {
    //     headers: {
    //       token: sessionStorage.getItem(JWT_STORAGE_KEY),
    //     },
    //   })
    orderClient
      .post('api/v1/order/saveLocation', payload, {
        headers: {
          token: sessionStorage.getItem(JWT_STORAGE_KEY),
        },
      })
      .then((res) => res?.data);
  return useMutation({
    mutationFn,
    onSuccess: () => {},
    onError: (error) => {
      console.error('❌ Save location failed:', error.response?.data || error.message);
      notify('error', error.response?.data?.error || 'Failed to save location');
    },
  });
};

/**
 * Save Locations Quick Access On Home screen
 * @returns
 */
export const useDeleteSavedLocation = () => {
  const mutationFn = (payload) =>
    orderClient
      .post('api/v1/order/deleteFavoriteLocation', payload, {
        headers: {
          token: sessionStorage.getItem(JWT_STORAGE_KEY),
        },
      })
      .then((res) => res?.data);
  return useMutation({
    mutationFn,
    onSuccess: () => {},
  });
};

/**
 * Update Saved Location Name
 * @returns
 */
export const useUpdateSavedLocation = () => {
  const mutationFn = (payload) =>
    orderClient
      .post('api/v1/order/updateLocation', payload, {
        headers: {
          token: sessionStorage.getItem(JWT_STORAGE_KEY),
        },
      })
      .then((res) => res?.data);
  return useMutation({
    mutationFn,
    onSuccess: () => {},
  });
};

/**
 * Get Saved Location for Given Type
 * @returns
 */
export const useGetSavedLocation = (type) => {
  const userID = localStorage.getItem(USER_ID_STORAGE_KEY);
  const query = useQuery({
    queryKey: ['useGetSavedLocation', type],
    queryFn: () =>
      // orderClient
      //   .get('api/v1/order/getFavoriteLocation', {
      //     params: {
      //       userID,
      //       type,
      //     },
      //   })
      orderClient
        .get('api/v1/order/getSavedLocation', {
          params: {
            userID,
            type,
          },
        })
        .then((res) => res?.data),
    enabled: !!userID || !!type, // prevent auto-run if userId or driverID is undefined,
  });

  return query;
};

/**
 *
 * @returns Price and Invoice detail for given payload of trip
 */
export const useGetPriceOfOrder = () => {
  const mutationFn = (payload) =>
    orderClient
      .post('api/v2/order/priceOfOrder', payload)
      .then((res) => res?.data?.success || { error: res?.data?.error });
  return useMutation({
    mutationFn,
  });
};

/**
 *
 * @returns Place Order
 */
export const usePlaceOrder = () => {
  const mutationFn = (payload) =>
    orderClient.post('api/v1/order/placeOrder', payload).then((res) => res?.data);
  return useMutation({
    mutationFn,
    mutationKey: ['payment_usePlaceOrder'],
  });
};

/**
 * Get If User Can Order using PAD or not
 * @returns
 */
export const useGetPADStatus = () => {
  const userID = sessionStorage.getItem(USER_ID_STORAGE_KEY);
  const query = useQuery({
    queryKey: ['getPADStatus'],
    queryFn: () =>
      orderClient
        .get('api/v1/order/getPAD', {
          params: {
            userID,
          },
        })
        .then((res) => res?.data),
    enabled: !!userID, // prevent auto-run if userId or driverID is undefined
  });

  return query;
};

/**
 *
 * @returns Add Favorite Order
 */
export const useAddFavoriteOrder = () => {
  const userID = localStorage.getItem(USER_ID_STORAGE_KEY);
  const mutationFn = (payload) =>
    orderClient.post('api/v1/order/addOrderName', { ...payload, userID }).then((res) => res?.data);
  return useMutation({
    mutationFn,
    mutationKey: ['Order_useAddFavoriteOrder'],
  });
};
